import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Badge, Button, Icon } from '@syneto/compass-react';
import useRemoteSupportStatus from '../../../../hooks/useRemoteSupportStatus';
import { getApiErrorMessage } from '../../../../helpers/apiError';
import {
	REMOTE_SUPPORT_ALLOW_ERROR_MESSAGE,
	REMOTE_SUPPORT_JOB_POLL,
	REMOTE_SUPPORT_OVERRIDE_DAYS,
	REMOTE_SUPPORT_STATUS,
	describeRemoteSupportStatus
} from '../../../../helpers/remoteSupport';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../../../services/jobs';
import { createRemoteSupportOverride } from '../../../../services/remoteSupport';
import { pushSuccessfulNotification } from '../../../../data/notifications/actions';
import AllowRemoteSupportModal from './AllowRemoteSupportModal';

/*
 * Says whether a machine's support tunnel can be opened and, when it cannot, why and how to allow it
 * for 7 days. The verdict is the server's (`allowed`, `reason`, `allowedUntil`); after an override is
 * saved the status is read again rather than assumed, so the card shows the end date Central computed.
 * When the status cannot be loaded the card says so and offers nothing: an "Allow" on top of a guess
 * could hide a tunnel that is already open.
 */
const RemoteSupportCard = ({ productId, serialNumber, pushSuccessfulNotification }) => {
	const { status, remoteSupport, error, reload } = useRemoteSupportStatus(productId);
	const [showAllowModal, setShowAllowModal] = useState(false);
	const pollAbortControllerRef = useRef(null);

	useEffect(() => {
		return () => {
			pollAbortControllerRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		setShowAllowModal(false);
	}, [productId]);

	const allowRemoteSupport = async (reason) => {
		const abortController = new AbortController();
		pollAbortControllerRef.current = abortController;

		try {
			const response = await createRemoteSupportOverride(productId, reason);
			const jobId = response?.data?.jobId;

			if (jobId) {
				const job = await pollJobUntilTerminal(jobId, { ...REMOTE_SUPPORT_JOB_POLL, signal: abortController.signal });

				if (!isJobSucceeded(job)) {
					throw new Error(describeJobOutcome(job, {
						failureMessage: REMOTE_SUPPORT_ALLOW_ERROR_MESSAGE,
						unappliedMessage: 'Remote support was not allowed'
					}));
				}
			}
		} catch (saveError) {
			if (saveError?.name === 'AbortError') {
				return;
			}

			throw new Error(getApiErrorMessage(saveError, REMOTE_SUPPORT_ALLOW_ERROR_MESSAGE));
		}

		if (abortController.signal.aborted) {
			return;
		}

		setShowAllowModal(false);
		pushSuccessfulNotification(`Remote support allowed for ${REMOTE_SUPPORT_OVERRIDE_DAYS} days.`);
		reload();
	};

	const renderBody = () => {
		if (status === REMOTE_SUPPORT_STATUS.LOADING) {
			return (
				<div className="text-muted">
					<Icon name="fa fa-circle-o-notch fa-spin me-2" />
					Checking whether the support tunnel can be opened…
				</div>
			);
		}

		if (status === REMOTE_SUPPORT_STATUS.FAILED) {
			return (
				<div>
					<Badge appearance="warning">Unknown</Badge>
					<span className="ms-2">The remote support status could not be loaded.</span>
					{error && <div className="text-muted mt-1" style={{ fontSize: '13px' }}>{error}</div>}
				</div>
			);
		}

		const description = describeRemoteSupportStatus(remoteSupport);

		return (
			<div className="d-flex align-items-center justify-content-between flex-wrap">
				<div>
					<Badge appearance={description.allowed ? 'success' : 'danger'}>
						{description.allowed ? 'Allowed' : 'Not allowed'}
					</Badge>
					<span className="ms-2 font-weight-500">{description.summary}</span>
					{description.explanation && (
						<div className="text-muted mt-1" style={{ fontSize: '13px' }}>
							{description.explanation}
							{description.allowedUntil && <span> Until {description.allowedUntil}.</span>}
						</div>
					)}
				</div>

				{description.canAllow && (
					<Button role="tertiary" onClick={() => { setShowAllowModal(true); }}>
						Allow remote support for {REMOTE_SUPPORT_OVERRIDE_DAYS} days
					</Button>
				)}
			</div>
		);
	};

	if (!productId) {
		return null;
	}

	return (
		<div className="remote-support-card row m-0 mb-4 port-status-box align-items-center" data-testid="remote-support-card">
			<div className="col-12 py-2">
				<label className="m-0">Remote support</label>
				{renderBody()}
			</div>

			{showAllowModal && (
				<AllowRemoteSupportModal
					serialNumber={serialNumber}
					onHide={() => { setShowAllowModal(false); }}
					onConfirm={allowRemoteSupport}
				/>
			)}
		</div>
	);
};

RemoteSupportCard.propTypes = {
	productId: PropTypes.string,
	serialNumber: PropTypes.string,
	pushSuccessfulNotification: PropTypes.func.isRequired
};

const mapDispatchToProps = (dispatch) => {
	return {
		pushSuccessfulNotification: (message) => { return dispatch(pushSuccessfulNotification(message)); }
	};
};

export { RemoteSupportCard };

export default connect(null, mapDispatchToProps)(RemoteSupportCard);
